import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'

import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { StatusBadge, type StatusTone } from '../../ui/StatusBadge/StatusBadge'
import { Switch } from '../../ui/Switch/Switch'
import {
  blockedKnownIdKeys,
  buildOperatoryPolicyPayload,
  normalizeOperatoryPolicy,
  operatoryIdKey,
  type OperatoryPolicy,
} from './operatory-policy-model'
import {
  isOperatoryPermissionError,
  type OperatoryPolicyRepository,
} from './operatory-policy-repository'
import type { ProviderResourceSettings } from './provider-resource-contracts'
import type { ProviderResourceRepository } from './provider-resource-repository'
import { AgeSchedulingPolicySettings } from './AgeSchedulingPolicySettings'
import { SettingsSaveFooter } from './SettingsSaveFooter'
import { useReportUnsavedChanges } from './settings-unsaved-changes'
import { TechnicalDetailsPanel } from './TechnicalDetailsPanel'
import panel from './SettingsSection.module.css'
import styles from './AppointmentsSettings.module.css'

type ProviderResources = NonNullable<
  ProviderResourceSettings['providerResources']
>

function setsEqual(left: ReadonlySet<string>, right: ReadonlySet<string>) {
  if (left.size !== right.size) return false
  return [...left].every((value) => right.has(value))
}

const SYNC: Record<'GREEN' | 'RED' | 'UNKNOWN', [StatusTone, string]> = {
  GREEN: ['success', 'Healthy'],
  RED: ['warning', 'Not healthy'],
  UNKNOWN: ['neutral', 'Unknown'],
}

function PmsResources({ resources }: { resources: ProviderResources }) {
  const [readTone, readLabel] = SYNC[resources.syncHealth.read]
  const [writeTone, writeLabel] = SYNC[resources.syncHealth.write]
  const providers = resources.providers
    .map((item) => item.displayLabel)
    .filter((label): label is string => Boolean(label))
  return (
    <>
      <div className={styles.syncRow}>
        <span>
          Read <StatusBadge tone={readTone}>{readLabel}</StatusBadge>
        </span>
        <span>
          Write <StatusBadge tone={writeTone}>{writeLabel}</StatusBadge>
        </span>
        <span>
          Freshness{' '}
          <StatusBadge
            tone={resources.freshness === 'CURRENT' ? 'success' : 'warning'}
          >
            {resources.freshness === 'CURRENT' ? 'Current' : 'Refresh needed'}
          </StatusBadge>
        </span>
        <small>
          Resource revision {resources.resourceRevision} · observation{' '}
          {resources.observationRevision}
        </small>
      </div>

      <dl className={styles.counts}>
        <div>
          <dt>Providers</dt>
          <dd>{resources.counts.providers}</dd>
        </div>
        <div>
          <dt>Operatories</dt>
          <dd>{resources.counts.operatories}</dd>
        </div>
        <div>
          <dt>Appointment types</dt>
          <dd>{resources.counts.appointmentTypes}</dd>
        </div>
        <div>
          <dt>PMS working hours</dt>
          <dd>{resources.counts.workingHours}</dd>
        </div>
      </dl>

      <div className={styles.resourceLists}>
        <div className={styles.resourceList}>
          <h3>Appointment types</h3>
          {resources.appointmentTypes.length ? (
            <ul className={styles.typeList}>
              {resources.appointmentTypes.map((item) => (
                <li key={`${item.name}-${item.minutes}`}>
                  <span>{item.name}</span>
                  <small>{item.minutes} min</small>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.none}>No appointment types supplied</p>
          )}
        </div>
        <div className={styles.resourceList}>
          <h3>Operatories</h3>
          {resources.operatories.length ? (
            <ul className={styles.chipList}>
              {resources.operatories.map((item) => (
                <li key={item.name}>{item.name}</li>
              ))}
            </ul>
          ) : (
            <p className={styles.none}>No operatories supplied</p>
          )}
        </div>
        <div className={`${styles.resourceList} ${styles.wide}`}>
          <h3>Providers</h3>
          {providers.length ? (
            <ul className={`${styles.chipList} ${styles.scrollList}`}>
              {providers.map((label, index) => (
                <li key={`${label}-${index}`}>{label}</li>
              ))}
            </ul>
          ) : (
            <p className={styles.none}>No provider labels supplied</p>
          )}
        </div>
      </div>
      <p className={styles.footnote}>
        PMS working hours are scheduling evidence only. They do not replace Flio
        business hours, which are managed in Working Hours; services are managed
        in Services.
      </p>
    </>
  )
}

export function AppointmentsSettings({
  tenantId,
  tenantLabel,
  repository,
  providerResourceRepository,
  showPmsResources = false,
}: {
  tenantId: string
  tenantLabel: string
  repository: OperatoryPolicyRepository
  providerResourceRepository?: ProviderResourceRepository
  /** Owners and Admins only: see `canViewClinicTechnicalDetails`. */
  showPmsResources?: boolean
}) {
  const providerQuery = useQuery({
    queryKey: ['settings', tenantId, 'provider-resources'],
    queryFn: () => providerResourceRepository!.getResources(tenantId),
    enabled: Boolean(providerResourceRepository),
  })
  const useLegacyPolicy =
    !providerResourceRepository ||
    (providerQuery.isSuccess &&
      providerQuery.data.authorityMode === 'TENANT_SECRET')
  const policyQuery = useQuery({
    queryKey: ['settings', tenantId, 'operatory-policy'],
    queryFn: async () =>
      normalizeOperatoryPolicy(await repository.getPolicy(tenantId)),
    enabled: useLegacyPolicy,
  })
  const [baseline, setBaseline] = useState<OperatoryPolicy | null>(null)
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set())
  const [overlappingSlots, setOverlappingSlots] = useState(false)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>(
    'idle'
  )

  useEffect(() => {
    setBaseline(null)
    setBlockedIds(new Set())
    setOverlappingSlots(false)
    setSaveStatus('idle')
  }, [tenantId])

  useEffect(() => {
    if (!policyQuery.data) return
    setBaseline(policyQuery.data)
    setBlockedIds(blockedKnownIdKeys(policyQuery.data))
    setOverlappingSlots(policyQuery.data.trueOverlappingOperatorySlots)
    setSaveStatus('idle')
  }, [policyQuery.data])

  const baselineBlockedIds = useMemo(
    () => (baseline ? blockedKnownIdKeys(baseline) : new Set<string>()),
    [baseline]
  )
  const isDirty = Boolean(
    baseline &&
    (!setsEqual(blockedIds, baselineBlockedIds) ||
      overlappingSlots !== baseline.trueOverlappingOperatorySlots)
  )
  useReportUnsavedChanges('appointments:operatories', isDirty)

  const savePolicy = useMutation({
    mutationFn: async () => {
      if (!baseline) throw new Error('Operatory policy is unavailable')
      return repository.updatePolicy(
        tenantId,
        buildOperatoryPolicyPayload(baseline, blockedIds, overlappingSlots)
      )
    },
    onSuccess: (savedPolicy) => {
      if (!baseline) return
      const updatedPolicy = {
        ...baseline,
        blockedOperatories: savedPolicy.blockedOperatories,
        trueOverlappingOperatorySlots:
          savedPolicy.trueOverlappingOperatorySlots,
      }
      setBaseline(updatedPolicy)
      setBlockedIds(blockedKnownIdKeys(updatedPolicy))
      setOverlappingSlots(updatedPolicy.trueOverlappingOperatorySlots)
      setSaveStatus('saved')
    },
    onError: () => setSaveStatus('error'),
  })
  if (providerResourceRepository && providerQuery.isPending) {
    return <Spinner label="Loading provider resources" />
  }
  if (providerResourceRepository && providerQuery.isError) {
    return (
      <StatePanel
        kind="error"
        title="Provider authority unavailable"
        description="The scheduling source of truth could not be determined safely. No appointment settings can be changed."
      />
    )
  }
  if (
    providerResourceRepository &&
    providerQuery.isSuccess &&
    providerQuery.data.authorityMode !== 'TENANT_SECRET' &&
    providerQuery.data.authorityMode !== 'PROVIDER_ACCOUNT_V1'
  ) {
    return (
      <StatePanel
        kind="error"
        title="Provider authority unavailable"
        description="The scheduling source of truth is unknown. No appointment settings can be changed."
      />
    )
  }
  if (
    providerQuery.data?.authorityMode === 'PROVIDER_ACCOUNT_V1' &&
    providerQuery.data.providerResources === null
  ) {
    const authorityPending = providerQuery.data.status === 'AUTHORITY_PENDING'
    return (
      <StatePanel
        kind="error"
        title={
          authorityPending
            ? 'Provider connection activation required'
            : 'Provider resource refresh required'
        }
        description={
          authorityPending
            ? 'This clinic’s provider connection is not active yet.'
            : 'This clinic is connected to its PMS, but read-only provider facts are not ready yet.'
        }
      />
    )
  }
  if (
    providerQuery.data?.authorityMode === 'PROVIDER_ACCOUNT_V1' &&
    providerQuery.data.providerResources
  ) {
    const resources = providerQuery.data.providerResources
    return (
      <section className={panel.section} aria-label="Appointment settings">
        <AgeSchedulingPolicySettings
          tenantId={tenantId}
          activePolicy={resources.flioSchedulingPolicy}
          repository={providerResourceRepository}
        />
        {showPmsResources ? (
          <TechnicalDetailsPanel
            eyebrow="Appointment settings"
            title="Synced PMS resources"
            description={
              <>
                Providers, operatories and appointment types for{' '}
                <strong>{tenantLabel}</strong>, read from NexHealth and managed
                in the PMS.
              </>
            }
          >
            <PmsResources resources={resources} />
          </TechnicalDetailsPanel>
        ) : null}
      </section>
    )
  }

  function setAvailable(id: string, available: boolean) {
    if (savePolicy.isPending) return
    setBlockedIds((current) => {
      const next = new Set(current)
      if (available) next.delete(id)
      else next.add(id)
      return next
    })
    setSaveStatus('idle')
  }

  function save() {
    if (!isDirty || savePolicy.isPending) return
    setSaveStatus('idle')
    savePolicy.mutate()
  }

  function discard() {
    if (!baseline || savePolicy.isPending) return
    setBlockedIds(blockedKnownIdKeys(baseline))
    setOverlappingSlots(baseline.trueOverlappingOperatorySlots)
    setSaveStatus('idle')
  }

  return (
    <section className={panel.section} aria-labelledby="operatory-policy-title">
      <div className={panel.panel}>
        <header className={panel.panelHeader}>
          <div className={panel.heading}>
            <span className={panel.eyebrow}>Appointment settings</span>
            <h2 id="operatory-policy-title">Operatory policy</h2>
            <p>
              Choose which synchronized operatories Flio may use when finding
              appointment availability for <strong>{tenantLabel}</strong>.
            </p>
          </div>
        </header>

        {policyQuery.isPending ? (
          <div className={styles.body}>
            <Spinner label="Loading appointment settings" />
          </div>
        ) : null}
        {policyQuery.isError ? (
          <div className={styles.body}>
            <StatePanel
              kind={
                isOperatoryPermissionError(policyQuery.error)
                  ? 'forbidden'
                  : 'error'
              }
              title="Appointment settings unavailable"
              description={
                isOperatoryPermissionError(policyQuery.error)
                  ? 'You do not have permission to manage these appointment settings.'
                  : 'These appointment settings could not be loaded.'
              }
            />
          </div>
        ) : null}
        {policyQuery.isSuccess && policyQuery.data === null ? (
          <div className={styles.body}>
            <StatePanel
              kind="error"
              title="Appointment settings unavailable"
              description="The stored operatory policy is malformed and cannot be edited safely."
            />
          </div>
        ) : null}

        {baseline ? (
          <>
            <div className={styles.body}>
              <div className={styles.blockHeader}>
                <h3>Operatories available to Flio</h3>
                <p>
                  Blocking an operatory removes its slots from availability
                  shown by Flio. It does not change the operatory in NexHealth.
                </p>
              </div>
              {baseline.operatories.length ? (
                <ul className={styles.operatories}>
                  {baseline.operatories.map((operatory) => {
                    const id = operatoryIdKey(operatory.id)
                    const available = !blockedIds.has(id)
                    return (
                      <li key={id}>
                        <label className={styles.operatoryRow}>
                          <span>
                            <strong>{operatory.name}</strong>
                            <small>
                              {available
                                ? 'Available for Flio booking'
                                : 'Blocked from Flio booking'}
                            </small>
                          </span>
                          <input
                            type="checkbox"
                            checked={available}
                            disabled={savePolicy.isPending}
                            aria-label={`${operatory.name} available for Flio booking`}
                            onChange={(event) =>
                              setAvailable(id, event.currentTarget.checked)
                            }
                          />
                        </label>
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <div className={panel.notice} data-tone="warning">
                  <ShieldAlert aria-hidden="true" size={17} />
                  <div>
                    <strong>No synchronized operatories</strong>
                    <p>
                      Operatory availability cannot be configured until
                      NexHealth operatory data has been synchronized.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.body}>
              <label className={styles.policySwitchRow}>
                <span>
                  <strong>Include overlapping operatory slots</strong>
                  <small>
                    When enabled, Flio asks NexHealth to include overlapping
                    operatory slots in availability searches.
                  </small>
                </span>
                <Switch
                  aria-label="Include overlapping operatory slots"
                  checked={overlappingSlots}
                  disabled={savePolicy.isPending}
                  onChange={(event) => {
                    setOverlappingSlots(event.currentTarget.checked)
                    setSaveStatus('idle')
                  }}
                />
              </label>
            </div>

            <SettingsSaveFooter
              dirty={isDirty}
              saving={savePolicy.isPending}
              saved={saveStatus === 'saved' && !isDirty}
              error={
                saveStatus === 'error'
                  ? 'Settings could not be saved. Your changes are still here; try again.'
                  : null
              }
              canSave
              onDiscard={discard}
              onSave={save}
            />
          </>
        ) : null}
      </div>
    </section>
  )
}
